import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Close,
  PlayCircleOutline,
  Schedule
} from '@mui/icons-material';
import { format } from 'date-fns';
import { Inspection } from './types';

interface StartInspectionDialogProps {
  open: boolean;
  inspection: Inspection | null;
  onClose: () => void;
  onConfirmStart: (inspectionId: string) => Promise<void>;
  loading?: boolean;
}

/**
 * StartInspectionDialog - Confirmation dialog for starting an inspection
 */
const StartInspectionDialog: React.FC<StartInspectionDialogProps> = ({
  open,
  inspection,
  onClose,
  onConfirmStart,
  loading = false
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleConfirmStart = async () => {
    if (inspection) {
      await onConfirmStart(inspection.inspectionId);
    }
  };

  if (!inspection) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: isMobile ? 0 : 2,
          m: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100vh' : '90vh'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: 1,
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PlayCircleOutline 
            color="primary" 
            sx={{ fontSize: { xs: '1.5rem', sm: '1.75rem' } }} 
          />
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            component="h2"
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '1.125rem', sm: '1.25rem' }
            }}
          >
            Start Inspection
          </Typography>
        </Box>
        
        <IconButton
          onClick={onClose}
          size={isMobile ? "medium" : "large"}
          sx={{ 
            color: 'text.secondary',
            '&:hover': { bgcolor: 'action.hover' }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      {/* Content */}
      <DialogContent sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Schedule 
            sx={{ 
              fontSize: { xs: '3rem', sm: '4rem' },
              color: 'warning.main',
              mb: 2
            }} 
          />
          
          <Typography 
            variant={isMobile ? "h6" : "h5"}
            gutterBottom
            sx={{ 
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: '1.125rem', sm: '1.25rem' }
            }}
          >
            Are you sure you want to start this inspection?
          </Typography>
          
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              mb: 3,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.5
            }}
          >
            Once started, the inspection status will be updated to "In Progress" and you can proceed with the inspection checklist.
          </Typography>
        </Box>

        {/* Inspection Details */}
        <Box sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: { xs: 2, sm: 2.5 },
          mb: 2
        }}>
          <Typography 
            variant="subtitle2" 
            color="primary"
            gutterBottom
            sx={{ 
              fontWeight: 600,
              fontSize: { xs: '0.875rem', sm: '0.925rem' }
            }}
          >
            Inspection Details
          </Typography>
          
          <Box sx={{ mt: 1.5, '& > *': { mb: 1 } }}>
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              <strong>Application:</strong> {inspection.applicationTitle}
            </Typography>
            
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              <strong>Application #:</strong> {inspection.applicationNumber}
            </Typography>
            
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              <strong>Type:</strong> {inspection.inspectionType}
            </Typography>
            
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              <strong>Date:</strong> {format(new Date(inspection.inspectionDate), 'MMM dd, yyyy')}
            </Typography>
            
            <Typography 
              variant="body2"
              sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
            >
              <strong>Time:</strong> {inspection.timeSlot}
            </Typography>
            
            <Typography 
              variant="body2"
              sx={{ 
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                wordBreak: 'break-word'
              }}
            >
              <strong>Location:</strong> {inspection.propertyAddress}
            </Typography>
          </Box>
        </Box>

        {/* Warning Message */}
        <Box sx={{
          bgcolor: 'warning.light',
          border: '1px solid',
          borderColor: 'warning.main',
          borderRadius: 1,
          p: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1
        }}>
          <Schedule 
            sx={{ 
              color: 'warning.dark',
              fontSize: '1.25rem',
              mt: 0.25
            }} 
          />
          <Typography 
            variant="body2"
            sx={{ 
              color: 'warning.dark',
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              lineHeight: 1.4
            }}
          >
            <strong>Note:</strong> Make sure you have reviewed all inspection requirements and have the necessary tools before starting the inspection.
          </Typography>
        </Box>
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm: 2.5 },
        gap: { xs: 1, sm: 1.5 },
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        '& > :not(style) ~ :not(style)': {
          marginLeft: 0
        }
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          color="inherit"
          disabled={loading}
          fullWidth={isMobile}
          sx={{
            fontSize: { xs: '0.875rem', sm: '0.925rem' },
            py: { xs: 1.25, sm: 1 },
            px: { xs: 3, sm: 4 },
            fontWeight: 500
          }}
        >
          Cancel
        </Button>
        
        <Button
          onClick={handleConfirmStart}
          variant="contained"
          color="primary"
          disabled={loading}
          fullWidth={isMobile}
          startIcon={loading ? null : <PlayCircleOutline />}
          sx={{
            fontSize: { xs: '0.875rem', sm: '0.925rem' },
            py: { xs: 1.25, sm: 1 },
            px: { xs: 3, sm: 4 },
            fontWeight: 600,
            minHeight: { xs: 48, sm: 40 }
          }}
        >
          {loading ? 'Starting...' : 'Yes, Start Inspection'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StartInspectionDialog;